import { useEffect, useState, useMemo, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { FileText, FileCsv, FileXls, DownloadSimple, Printer, MagnifyingGlass, ArrowsClockwise, CheckCircle } from "@phosphor-icons/react";

/** Phase 6D — Catalog-driven reports console.
 *  Loads /api/reports/catalog (permission-aware) → dispatches to /api/reports/render/{key}
 *  with a shared filter contract. Exports match the filtered dataset exactly.
 */
const PRESETS = [
  { key: "last7", label: "Last 7 days", days: 7 },
  { key: "last30", label: "Last 30 days", days: 30 },
  { key: "last90", label: "Last 90 days", days: 90 },
  { key: "mtd", label: "Month to date", mtd: true },
  { key: "ytd", label: "Year to date", ytd: true },
  { key: "custom", label: "Custom", custom: true },
];

function dateRange(preset, customFrom, customTo) {
  const today = new Date();
  const iso = (d) => d.toISOString().slice(0, 10);
  if (preset.custom) return { date_from: customFrom, date_to: customTo };
  if (preset.mtd) return { date_from: iso(new Date(today.getFullYear(), today.getMonth(), 1)), date_to: iso(today) };
  if (preset.ytd) return { date_from: iso(new Date(today.getFullYear(), 0, 1)), date_to: iso(today) };
  const from = new Date(today); from.setDate(today.getDate() - preset.days);
  return { date_from: iso(from), date_to: iso(today) };
}

export default function Reports() {
  const [catalog, setCatalog] = useState([]);
  const [selected, setSelected] = useState(null);
  const [preset, setPreset] = useState(PRESETS[1]);
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [team, setTeam] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [validation, setValidation] = useState(null);
  const [validating, setValidating] = useState(false);
  const [employees, setEmployees] = useState([]);

  useEffect(() => {
    api.get("/reports/catalog").then((r) => {
      const rows = r.data.rows || [];
      setCatalog(rows);
      setSelected((prev) => prev || rows[0] || null);
    }).catch(() => toast.error("Failed to load report catalog"));
    api.get("/directory/employees?page_size=200").then((r) => setEmployees(r.data.rows || [])).catch(() => {});
  }, []);

  const teams = useMemo(() => [...new Set(employees.map((e) => e.team).filter(Boolean))].sort(), [employees]);

  const params = useMemo(() => {
    const r = dateRange(preset, customFrom, customTo);
    return {
      ...r, employee_id: employeeId || undefined, team: team || undefined,
      q: q || undefined, page, page_size: pageSize,
    };
  }, [preset, customFrom, customTo, employeeId, team, q, page, pageSize]);

  const isLocal = (key) => [
    "team-leader-performance", "hr-performance", "candidate-status", "reassignments",
    "approval-workflow", "placements", "offers", "joinings",
  ].includes(key);

  const legacyPath = (key) => {
    if (["executive-performance", "recruiter-performance", "employee-productivity"].includes(key)) return "/performance/rankings?period=month";
    if (key === "candidate-aging") return "/reports/candidate-aging";
    if (key === "assignments") return "/reports/assignments";
    if (key === "capacity") return "/reports/capacity";
    if (key === "incentives") return "/incentives-report?period=monthly";
    if (key === "audit") return "/audit-logs/query?page_size=100";
    return null;
  };

  const load = useCallback(async () => {
    if (!selected) return;
    setLoading(true); setData(null);
    try {
      let url;
      if (isLocal(selected.key)) {
        const qs = Object.entries(params).filter(([, v]) => v !== undefined && v !== "").map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
        url = `/reports/render/${selected.key}?format=json&${qs}`;
      } else {
        url = legacyPath(selected.key);
      }
      if (!url) throw new Error("Unsupported report");
      const r = await api.get(url);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load report");
    } finally { setLoading(false); }
  }, [selected, params]);

  useEffect(() => { setPage(1); }, [selected, preset, customFrom, customTo, employeeId, team, q]);
  useEffect(() => { load(); }, [load]);

  const doExport = async (format) => {
    if (!selected) return;
    if (!isLocal(selected.key)) {
      toast.info("Use the legacy report page for this format");
      return;
    }
    try {
      const qs = Object.entries(params).filter(([, v]) => v !== undefined && v !== "").map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
      const url = `/reports/render/${selected.key}?format=${format}&${qs}`;
      const r = await api.get(url, { responseType: "blob" });
      const blob = new Blob([r.data]);
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `${selected.key}_${new Date().toISOString().slice(0, 10)}.${format === "excel" ? "xlsx" : format}`;
      link.click();
    } catch (e) { toast.error("Export failed"); }
  };

  const runValidation = async () => {
    setValidating(true);
    try {
      const r = await api.post("/reports/validate-workflows");
      setValidation(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Validation failed"); }
    finally { setValidating(false); }
  };

  // Render rows generically — safe against varied backend response shapes
  const _rawRows = data?.rows ?? data?.all ?? data?.items ?? data;
  const rows = Array.isArray(_rawRows) ? _rawRows : [];
  const columns = rows.length ? Object.keys(rows[0]).filter((k) => k !== "id" && !k.startsWith("_")) : [];

  return (
    <div className="space-y-5" data-testid="reports-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-400">Insights</div>
          <h2 className="text-2xl font-semibold">Reports</h2>
          <p className="text-sm text-slate-500">All reports share one filter contract and exports match the filtered dataset exactly.</p>
        </div>
        <button onClick={runValidation} disabled={validating}
                data-testid="run-workflow-validation"
                className="text-sm border border-slate-200 rounded-md px-3 py-2 hover:bg-slate-50 inline-flex items-center gap-1 disabled:opacity-50">
          <CheckCircle size={14} /> {validating ? "Validating…" : "Run workflow validation"}
        </button>
      </div>

      {validation && (
        <div className={`border rounded-md p-3 text-sm ${validation.failed ? "bg-red-50 border-red-200 text-red-800" : "bg-emerald-50 border-emerald-200 text-emerald-800"}`}
             data-testid="validation-panel">
          <div className="font-semibold mb-2">
            Workflow validation — {validation.passed}/{validation.total} passed
          </div>
          <ul className="space-y-1">
            {validation.checks.map((c) => (
              <li key={c.name} className={`text-xs ${c.passed ? "text-emerald-700" : "text-red-700"}`}
                  data-testid={`validation-${c.name.slice(0, 20).replace(/\W/g, "-")}`}>
                {c.passed ? "✔" : "✖"} {c.name} — {c.detail}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-[240px_1fr] gap-4">
        {/* Catalog sidebar */}
        <div className="border border-slate-200 rounded-md overflow-hidden bg-white" data-testid="report-catalog">
          <div className="text-xs uppercase text-slate-500 px-3 py-2 bg-slate-50">Report catalog ({catalog.length})</div>
          {catalog.map((r) => (
            <button key={r.key} onClick={() => setSelected(r)}
                    data-testid={`report-item-${r.key}`}
                    className={`w-full text-left px-3 py-2 text-sm border-t border-slate-100 hover:bg-slate-50 ${selected?.key === r.key ? "bg-sky-50 font-medium" : ""}`}>
              {r.title}
            </button>
          ))}
        </div>

        {/* Filters + result */}
        <div className="space-y-3">
          <div className="bg-white border border-slate-200 rounded-md p-3 flex flex-wrap gap-2 items-center" data-testid="report-filters">
            <select value={preset.key} onChange={(e) => setPreset(PRESETS.find((p) => p.key === e.target.value))}
                    data-testid="report-preset"
                    className="border border-slate-200 rounded-md px-3 py-2 text-sm">
              {PRESETS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
            </select>
            {preset.custom && (
              <>
                <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)}
                       data-testid="report-date-from"
                       className="border border-slate-200 rounded-md px-3 py-2 text-sm" />
                <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)}
                       data-testid="report-date-to"
                       className="border border-slate-200 rounded-md px-3 py-2 text-sm" />
              </>
            )}
            <select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}
                    data-testid="report-employee-filter"
                    className="border border-slate-200 rounded-md px-3 py-2 text-sm min-w-[160px]">
              <option value="">All employees</option>
              {employees.map((e) => <option key={e.id} value={e.id}>{`${e.name} · ${e.employee_code || ""}`}</option>)}
            </select>
            <select value={team} onChange={(e) => setTeam(e.target.value)}
                    data-testid="report-team-filter"
                    className="border border-slate-200 rounded-md px-3 py-2 text-sm">
              <option value="">All teams</option>
              {teams.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <div className="relative flex-1 min-w-[180px]">
              <MagnifyingGlass size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)}
                     data-testid="report-search"
                     className="w-full border border-slate-200 rounded-md pl-8 pr-3 py-2 text-sm" />
            </div>
            <button onClick={load} title="Refresh" data-testid="report-refresh"
                    className="border border-slate-200 rounded-md px-3 py-2"><ArrowsClockwise size={14} /></button>
            {isLocal(selected?.key) && (
              <div className="flex gap-1">
                <button onClick={() => doExport("csv")} data-testid="report-export-csv"
                        className="text-sm border border-slate-200 rounded-md px-3 py-2 inline-flex items-center gap-1 hover:bg-slate-50">
                  <FileCsv size={14} /> CSV
                </button>
                <button onClick={() => doExport("excel")} data-testid="report-export-excel"
                        className="text-sm border border-slate-200 rounded-md px-3 py-2 inline-flex items-center gap-1 hover:bg-slate-50">
                  <FileXls size={14} /> Excel
                </button>
                <button onClick={() => doExport("pdf")} data-testid="report-export-pdf"
                        className="text-sm border border-slate-200 rounded-md px-3 py-2 inline-flex items-center gap-1 hover:bg-slate-50">
                  <Printer size={14} /> PDF
                </button>
              </div>
            )}
          </div>

          {loading && <div className="text-sm text-slate-400 py-10 text-center">Loading…</div>}
          {!loading && selected && (
            <div className="border border-slate-200 rounded-md overflow-hidden bg-white" data-testid={`report-result-${selected.key}`}>
              <div className="px-3 py-2 border-b border-slate-100 flex items-center justify-between">
                <div className="text-sm font-semibold">{selected.title}</div>
                <div className="text-xs text-slate-500">
                  {data?.total != null ? `${rows.length} of ${data.total}` : `${rows.length} rows`}
                </div>
              </div>
              <div className="overflow-auto max-h-[60vh]">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500 text-xs sticky top-0">
                    <tr>
                      {columns.map((c) => <th key={c} className="text-left px-3 py-2 whitespace-nowrap">{c.replace(/_/g, " ")}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 && (
                      <tr><td colSpan={columns.length || 1} className="text-center p-6 text-slate-400">No data.</td></tr>
                    )}
                    {rows.map((r, i) => (
                      <tr key={r.id || i} className="border-t border-slate-100" data-testid={`report-row-${i}`}>
                        {columns.map((c) => (
                          <td key={c} className="px-3 py-2 text-slate-700 whitespace-nowrap">
                            {typeof r[c] === "object" ? JSON.stringify(r[c]) : String(r[c] ?? "—").slice(0, 120)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {data?.total > pageSize && (
                <div className="flex justify-between items-center px-3 py-2 text-sm border-t border-slate-100">
                  <span className="text-slate-500">Page {page}</span>
                  <div className="flex gap-1">
                    <button disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
                            data-testid="report-page-prev"
                            className="text-xs px-2 py-1 border border-slate-200 rounded-md disabled:opacity-40">Prev</button>
                    <button disabled={rows.length < pageSize} onClick={() => setPage((p) => p + 1)}
                            data-testid="report-page-next"
                            className="text-xs px-2 py-1 border border-slate-200 rounded-md disabled:opacity-40">Next</button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
